/**
 * The pick step of the tool editor: the account's tools, searched by name, narrowed to one integration
 * or to the ones that read or write, and grouped by integration with the ones this dataset already
 * expects first. Clicking a tool opens it beside the list as a signature.
 */

import type { KeyboardEvent } from 'react';
import { Loader2 } from 'lucide-react';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { HighlightedText } from '@/components/ui/highlighted-text';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { matchesAllTerms } from '@/lib/search';
import { cn } from '@/lib/utils';
import { IntegrationLogo } from '@/modules/integrations';
import { humanize } from '@/modules/simulator/ui/text';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { AccessFilter, AccessIcon } from './tool-access';
import { ToolDetails } from './tool-details';
import type { ToolFilter } from './tool-filters';
import { toolKey } from './tool-schema';
import type { ToolCatalogue } from './use-tool-catalogue';

const SUGGESTED = 'suggested|';

const ALL_INTEGRATIONS = 'all';

// Keys pressed in the filters and the details are theirs; the list would take Enter as a pick.
const keepKeys = (event: KeyboardEvent) => event.stopPropagation();

const IntegrationFilter = ({
	providers,
	value,
	onChange
}: {
	providers: string[];
	value: string | null;
	onChange: (provider: string | null) => void;
}) => (
	<Select value={value ?? ALL_INTEGRATIONS} onValueChange={(next) => onChange(next === ALL_INTEGRATIONS ? null : next)}>
		<SelectTrigger size='sm' aria-label='Integration' className='w-44 rounded-full border-gray-500'>
			<SelectValue />
		</SelectTrigger>
		<SelectContent>
			<SelectItem value={ALL_INTEGRATIONS} className='text-xs'>
				All integrations
			</SelectItem>
			{providers.map((provider) => (
				<SelectItem key={provider} value={provider} className='text-xs'>
					<IntegrationLogo provider={provider} className='size-4' decorative />
					{humanize(provider)}
				</SelectItem>
			))}
		</SelectContent>
	</Select>
);

interface ToolPickerProps {
	catalogue: ToolCatalogue;
	query: string;
	onQueryChange: (query: string) => void;
	filter: ToolFilter;
	onFilterChange: (filter: ToolFilter) => void;
	/** The clicked tool's `toolKey`, and the tool itself while the list still shows it. */
	chosen: string | null;
	chosenTool: ToolDefinitionAction | null;
	onChoose: (key: string) => void;
}

export const ToolPicker = ({
	catalogue,
	query,
	onQueryChange,
	filter,
	onFilterChange,
	chosen,
	chosenTool,
	onChoose
}: ToolPickerProps) => {
	const { isLoading, isError, terms, providers, suggested, groups } = catalogue;

	const item = (tool: ToolDefinitionAction, prefix = '') => {
		const key = toolKey(tool.provider, tool.tool);
		const isChosen = key === chosen;
		// Matched by its own name alone, so that name shows too.
		const byOwnName = terms.length > 0 && !matchesAllTerms(tool.label.toLowerCase(), terms);
		return (
			<CommandItem
				key={`${prefix}${key}`}
				value={`${prefix}${key}`}
				onSelect={() => onChoose(key)}
				aria-current={isChosen || undefined}
				className={cn(
					'gap-2 py-2',
					isChosen && 'bg-indigo-50 text-indigo-800 data-[selected=true]:bg-indigo-50 data-[selected=true]:text-indigo-800'
				)}
			>
				<IntegrationLogo provider={tool.provider} className='size-4' decorative />
				<span className='flex min-w-0 flex-1 flex-col'>
					<span className='truncate text-[13px] leading-4.5'>
						<HighlightedText text={tool.label} terms={terms} />
					</span>
					{byOwnName && (
						<span className='truncate font-mono text-[11px] leading-4 text-gray-800'>
							<HighlightedText text={tool.tool} terms={terms} />
						</span>
					)}
				</span>
				<AccessIcon riskLevel={tool.riskLevel} />
			</CommandItem>
		);
	};

	return (
		<Command shouldFilter={false} className='flex min-h-0 flex-1 flex-col rounded-none'>
			<div className='flex flex-wrap items-center gap-2 px-4 pt-3 pb-3'>
				<CommandInput
					value={query}
					onValueChange={onQueryChange}
					placeholder='Search tools by name'
					wrapperClassName='h-8 min-w-0 flex-1 basis-full rounded-full border border-gray-500 bg-white transition-colors focus-within:border-gray-600 hover:border-gray-600 @2xl:basis-0'
					iconClassName='size-3.5'
					className='h-8 py-0 text-[13px]'
				/>
				<div onKeyDown={keepKeys} className='flex shrink-0 items-center gap-2'>
					<IntegrationFilter
						providers={providers}
						value={filter.provider}
						onChange={(provider) => onFilterChange({ ...filter, provider })}
					/>
					<AccessFilter value={filter.access} onChange={(access) => onFilterChange({ ...filter, access })} />
				</div>
			</div>
			<div className='grid min-h-0 flex-1 overflow-y-auto border-t border-gray-500 @2xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] @2xl:overflow-hidden'>
				<CommandList className='max-h-64 border-b border-gray-500 @2xl:max-h-none @2xl:border-r @2xl:border-b-0'>
					{isLoading ? (
						<p className='flex items-center justify-center gap-2 p-6 text-xs text-gray-800'>
							<Loader2 className='size-3 animate-spin' />
							Loading the account&apos;s tools
						</p>
					) : (
						<CommandEmpty>{isError ? 'The tools could not be loaded.' : 'No tool matches.'}</CommandEmpty>
					)}
					{suggested.length > 0 && (
						<CommandGroup heading='Already in this dataset'>{suggested.map((tool) => item(tool, SUGGESTED))}</CommandGroup>
					)}
					{groups.map(([provider, list]) => (
						<CommandGroup key={provider} heading={humanize(provider)}>
							{list.map((tool) => item(tool))}
						</CommandGroup>
					))}
				</CommandList>
				<div onKeyDown={keepKeys} className='flex min-h-0 flex-col'>
					<ToolDetails tool={chosenTool} />
				</div>
			</div>
		</Command>
	);
};
